
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { GuestData } from '../types';

export const useGuestData = (eventId: string) => {
  const {
    data,
    isLoading,
    error
  } = useQuery({
    queryKey: ['rsvp-guest-data', eventId],
    queryFn: async (): Promise<{ available: GuestData[], responded: GuestData[], inProgress: GuestData[] }> => {
      const { data, error } = await supabase
        .from('event_invites')
        .select(`
          invite_id,
          display_name,
          phone_number,
          rsvp_status,
          responded_at,
          claimed,
          claimed_at,
          claimed_by_user_id,
          user:claimed_by_user_id(avatar_url, full_name),
          feedback_started,
          feedback_started_by
        `)
        .eq('event_id', eventId);

      if (error) throw error;

      // Split guests into available, in progress, and responded
      const available: GuestData[] = [];
      const responded: GuestData[] = [];
      const inProgress: GuestData[] = [];

      data.forEach(guest => {
        const guestData: GuestData = {
          inviteId: guest.invite_id,
          displayName: guest.display_name,
          phoneNumber: guest.phone_number,
          hasResponded: !!guest.responded_at,
          rsvpStatus: guest.rsvp_status as 'accepted' | 'declined' | null,
          avatar_url: guest.user?.avatar_url || null,
          claimed: guest.claimed,
          claimedAt: guest.claimed_at,
          claimedByUserId: guest.claimed_by_user_id, // This is now properly defined in GuestData
          claimedByName: guest.user?.full_name,
          feedbackStarted: guest.feedback_started,
          feedbackStartedBy: guest.feedback_started_by
        };

        // Simplified logic: if guest has responded (has a non-invited status), add to responded
        if (guest.rsvp_status === 'accepted' || guest.rsvp_status === 'declined') {
          responded.push(guestData);
        } 
        // In-progress: claimed but still has invited status
        else if (guest.claimed && guest.rsvp_status === 'invited') {
          inProgress.push(guestData);
        }
        // All others are available
        else {
          available.push(guestData);
        }
      });

      return { available, responded, inProgress };
    },
  });

  return {
    availableGuests: data?.available || [],
    respondedGuests: data?.responded || [],
    inProgressGuests: data?.inProgress || [],
    isLoading,
    error
  };
};
